import React, { useState, useEffect } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import axios from 'axios';
import API_BASE_URL from '../config';

const Login = ({ onLogin, setUser }) => {
  const navigate = useNavigate();
  const [formData, setFormData] = useState({
    email: '',
    password: ''
  });
  const [error, setError] = useState('');
  const [loading, setLoading] = useState(false);
  const [showPassword, setShowPassword] = useState(false);

  // Redirect to dashboard if already logged in
  useEffect(() => {
    const token = localStorage.getItem('token');
    const userData = localStorage.getItem('user');
    if (token && userData) {
      navigate('/dashboard');
    }
  }, [navigate]);

  const handleChange = (e) => {
    setFormData({
      ...formData,
      [e.target.name]: e.target.value
    });
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setLoading(true);
    setError('');

    try {
      // Use .php extension to avoid .htaccess rewrite issues with POST requests
      const response = await axios.post(`${API_BASE_URL}/auth/login.php`, formData);
      console.log('Login response:', response.data);
      
      // Handle different response formats
      const token = response.data?.token || response.data?.data?.token;
      const talent = response.data?.talent || response.data?.user || response.data?.data?.user || response.data?.data?.talent;
      
      if (!token) {
        console.error('No token in response:', response.data);
        setError('Login successful but no authentication token received. Please try again.');
        setLoading(false);
        return;
      }
      
      // Create user object if not provided
      const userData = talent || {
        email: formData.email,
        firstName: response.data?.firstName,
        lastName: response.data?.lastName,
        userType: response.data?.userType
      };
      
      localStorage.setItem('token', token);
      localStorage.setItem('user', JSON.stringify(userData));
      
      // Also set cookie as fallback (cookies are more reliable than headers on some hosts)
      document.cookie = `auth_token=${token}; path=/; max-age=86400; SameSite=Lax; Secure`;
      
      setUser(userData);
      onLogin(true);
      navigate('/dashboard');
    } catch (err) {
      console.error('Login error:', err);
      console.error('API URL being used:', `${API_BASE_URL}/auth/login`);
      console.error('Error message:', err.message);
      console.error('Error code:', err.code);
      
      if (err.response?.status === 401) {
        setError('Invalid email or password. Please try again.');
      } else if (err.response?.status >= 500) {
        setError('Server error. Please try again later.');
      } else if (err.message === 'Network Error' || err.code === 'ECONNREFUSED' || err.code === 'ERR_NETWORK') {
        const apiUrl = `${API_BASE_URL}/auth/login`;
        setError(`Unable to connect to the server at ${apiUrl}. Please check your internet connection and try again. If the problem persists, contact support.`);
      } else {
        setError(err.response?.data?.message || 'Login failed. Please try again.');
      }
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="form-container">
      <h2>Login</h2>
      <form onSubmit={handleSubmit}>
        <div className="form-group">
          <label htmlFor="email">Email:</label>
          <input
            type="email"
            id="email"
            name="email"
            value={formData.email}
            onChange={handleChange}
            required
          />
        </div>
        
        <div className="form-group">
          <label htmlFor="password">Password:</label>
          <div className="password-input-wrapper">
            <input
              type={showPassword ? 'text' : 'password'}
              id="password"
              name="password"
              value={formData.password}
              onChange={handleChange}
              required
              className="password-input"
            />
            <button
              type="button"
              className="password-toggle"
              onClick={() => setShowPassword(!showPassword)}
              aria-label={showPassword ? 'Hide password' : 'Show password'}
              tabIndex={0}
            >
              {showPassword ? (
                <span className="password-icon">👁️</span>
              ) : (
                <span className="password-icon">👁️‍🗨️</span>
              )}
            </button>
          </div>
        </div>

        {error && <div className="error">{error}</div>}
        
        <button type="submit" className="btn" disabled={loading}>
          {loading ? 'Logging in...' : 'Login'}
        </button>
      </form>
      
      <p style={{ textAlign: 'center', marginTop: '20px' }}>
        Don't have an account? <Link to="/register">Register here</Link>
      </p>
      <p style={{ textAlign: 'center', marginTop: '10px' }}>
        <Link to="/">← Back to Home</Link>
      </p>
    </div>
  );
};

export default Login;
